import { ReactNode } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { cn } from '../../utils/cn';

type BadgeVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'outline';

type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: BadgeSize;
  icon?: React.ComponentType<{ className?: string }>;
  removable?: boolean;
  onRemove?: () => void;
  className?: string;
  dot?: boolean;
  pulse?: boolean;
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-800 dark:bg-dark-700 dark:text-gray-200',
  primary: 'bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300',
  secondary: 'bg-secondary-100 text-secondary-800 dark:bg-secondary-900/30 dark:text-secondary-300',
  success: 'bg-success-100 text-success-700 dark:bg-success-700/20 dark:text-success-500',
  error: 'bg-error-100 text-error-700 dark:bg-error-700/20 dark:text-error-500',
  warning: 'bg-warning-100 text-warning-700 dark:bg-warning-700/20 dark:text-warning-500',
  info: 'bg-info-100 text-info-700 dark:bg-info-700/20 dark:text-info-500',
  outline: 'bg-transparent border border-gray-300 text-gray-700 dark:border-dark-600 dark:text-gray-300',
};

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-xs',
  lg: 'px-3 py-1 text-sm',
};

const dotColors: Record<BadgeVariant, string> = {
  default: 'bg-gray-500',
  primary: 'bg-primary-500',
  secondary: 'bg-secondary-500',
  success: 'bg-success-500',
  error: 'bg-error-500',
  warning: 'bg-warning-500',
  info: 'bg-info-500',
  outline: 'bg-gray-400',
};

export function Badge({
  children,
  variant = 'default',
  size = 'md',
  icon: Icon,
  removable = false,
  onRemove,
  className,
  dot = false,
  pulse = false,
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 font-medium rounded-full',
        variantClasses[variant],
        sizeClasses[size],
        className
      )}
    >
      {dot && (
        <span className="relative flex h-2 w-2">
          {pulse && (
            <span
              className={cn(
                'animate-ping absolute inline-flex h-full w-full rounded-full opacity-75',
                dotColors[variant]
              )}
            />
          )}
          <span
            className={cn('relative inline-flex rounded-full h-2 w-2', dotColors[variant])}
          />
        </span>
      )}
      {Icon && <Icon className="h-3 w-3" />}
      {children}
      {removable && (
        <button
          type="button"
          onClick={onRemove}
          className={cn(
            'ml-0.5 -mr-1 p-0.5 rounded-full',
            'hover:bg-black/10 dark:hover:bg-white/10',
            'transition-colors focus:outline-none'
          )}
        >
          <XMarkIcon className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}

interface StatusBadgeProps {
  status: 'active' | 'inactive' | 'pending' | 'success' | 'error' | 'warning';
  label?: string;
  className?: string;
}

const statusConfig: Record<
  StatusBadgeProps['status'],
  { variant: BadgeVariant; defaultLabel: string }
> = {
  active: { variant: 'success', defaultLabel: 'Active' },
  inactive: { variant: 'default', defaultLabel: 'Inactive' },
  pending: { variant: 'warning', defaultLabel: 'Pending' },
  success: { variant: 'success', defaultLabel: 'Success' },
  error: { variant: 'error', defaultLabel: 'Error' },
  warning: { variant: 'warning', defaultLabel: 'Warning' },
};

export function StatusBadge({ status, label, className }: StatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} dot pulse={status === 'pending'} className={className}>
      {label || config.defaultLabel}
    </Badge>
  );
}

interface CountBadgeProps {
  count: number;
  max?: number;
  variant?: BadgeVariant;
  className?: string;
}

export function CountBadge({
  count,
  max = 99,
  variant = 'primary',
  className,
}: CountBadgeProps) {
  const displayCount = count > max ? `${max}+` : count.toString();

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center',
        'min-w-[18px] h-[18px] px-1',
        'text-xs font-semibold rounded-full',
        variantClasses[variant],
        className
      )}
    >
      {displayCount}
    </span>
  );
}

interface DiscountBadgeProps {
  percentage: number;
  className?: string;
}

export function DiscountBadge({ percentage, className }: DiscountBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-1',
        'text-xs font-bold text-white',
        'bg-error-500 rounded',
        className
      )}
    >
      -{percentage}%
    </span>
  );
}

interface ProductTypeBadgeProps {
  type: 'brand_new' | 'recycled';
  className?: string;
}

export function ProductTypeBadge({ type, className }: ProductTypeBadgeProps) {
  const config = {
    brand_new: {
      label: 'Brand New',
      className: 'bg-success-500 text-white',
    },
    recycled: {
      label: 'Recycled',
      className: 'bg-info-500 text-white',
    },
  };

  const { label, className: typeClassName } = config[type];

  return (
    <span
      className={cn(
        'inline-flex items-center px-2 py-0.5',
        'text-xs font-semibold rounded',
        typeClassName,
        className
      )}
    >
      {label}
    </span>
  );
}

interface FeaturedBadgeProps {
  className?: string;
}

export function FeaturedBadge({ className }: FeaturedBadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5',
        'text-xs font-semibold rounded',
        'bg-gradient-to-r from-accent-400 to-primary-500 text-white',
        'shadow-sm',
        className
      )}
    >
      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
      Featured
    </span>
  );
}
